<script setup>
import { computed } from 'vue';
import { format } from 'date-fns';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  order: {
    type: Object,
    required: true,
  },
  modules: {
    type: Object,
    default: () => ({}),
  },
  capabilities: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const formattedDate = computed(() => {
  if (!props.order.created_at) {
    return '';
  }

  return format(new Date(props.order.created_at), 'MMM d, yyyy');
});

const formattedTotal = computed(() => {
  if (props.order.total === undefined || props.order.total === null) {
    return '';
  }

  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency: props.order.currency || 'USD',
  }).format(props.order.total);
});

const orderStatusLabel = computed(
  () => props.order.status || t('CONVERSATION_SIDEBAR.VTEX.STATUS_UNKNOWN')
);

const visibleItems = computed(() => (props.order.items || []).slice(0, 3));

const hiddenItemsCount = computed(() =>
  Math.max((props.order.items || []).length - visibleItems.value.length, 0)
);
const trackingEntries = computed(() => props.order.tracking || []);
const returnsHistory = computed(() => props.order.returns?.history || []);
const canShowTracking = computed(
  () => props.modules.tracking_sidebar && props.capabilities.tracking_sidebar
);
const canShowPayments = computed(
  () => props.modules.payments_sidebar && props.capabilities.payments_sidebar
);
const canShowReturns = computed(
  () => props.modules.returns_sidebar && props.capabilities.returns_sidebar
);

const formatReturnEntryMeta = entry =>
  [entry.created_at, entry.reason].filter(Boolean).join(' · ');
</script>

<template>
  <div
    class="rounded-xl border border-n-weak bg-n-card p-3 flex flex-col gap-3 shadow-sm"
  >
    <div class="flex flex-col gap-2">
      <div class="min-w-0">
        <a
          :href="order.admin_url"
          target="_blank"
          rel="noopener noreferrer"
          class="block text-sm font-semibold text-n-slate-12 hover:underline break-words"
        >
          {{ $t('CONVERSATION_SIDEBAR.VTEX.ORDER_ID', { id: order.id }) }}
        </a>
        <div
          class="mt-1 flex flex-wrap items-center gap-2 text-xs text-n-slate-11"
        >
          <span v-if="formattedDate">{{ formattedDate }}</span>
          <span v-if="formattedTotal" class="font-medium text-n-slate-12">
            {{ formattedTotal }}
          </span>
        </div>
      </div>
      <span
        class="self-start rounded-full bg-n-alpha-2 px-2.5 py-1 text-[11px] font-medium text-n-slate-11 max-w-full break-words"
      >
        {{ orderStatusLabel }}
      </span>
    </div>

    <div
      v-if="order.items?.length"
      class="rounded-lg bg-n-alpha-2 p-2.5 flex flex-col gap-2"
    >
      <div
        v-for="item in visibleItems"
        :key="`${order.id}-${item.sku_id}`"
        class="flex flex-col gap-1"
      >
        <span class="min-w-0 text-sm text-n-slate-11 leading-5 break-words">
          <span class="font-medium text-n-slate-12">
            {{
              $t('CONVERSATION_SIDEBAR.VTEX.ITEM_QUANTITY', {
                quantity: item.quantity,
              })
            }}
          </span>
          {{ item.name }}
        </span>
        <span
          v-if="item.price !== undefined && item.price !== null"
          class="text-xs text-n-slate-11"
        >
          {{
            new Intl.NumberFormat('en', {
              style: 'currency',
              currency: order.currency || 'USD',
            }).format(item.price)
          }}
        </span>
      </div>
      <span v-if="hiddenItemsCount" class="text-xs text-n-slate-10">
        {{
          $t('CONVERSATION_SIDEBAR.VTEX.MORE_ITEMS', {
            count: hiddenItemsCount,
          })
        }}
      </span>
    </div>

    <div
      v-if="canShowTracking && trackingEntries.length"
      class="rounded-lg border border-n-weak p-2.5 flex flex-col gap-2"
    >
      <p
        class="text-xs font-semibold uppercase tracking-[0.08em] text-n-slate-10"
      >
        {{ $t('CONVERSATION_SIDEBAR.VTEX.TRACKING_TITLE') }}
      </p>
      <div
        v-for="entry in trackingEntries"
        :key="`${order.id}-${entry.tracking_number || entry.carrier}`"
        class="text-sm text-n-slate-11 flex flex-col gap-1"
      >
        <span class="font-medium text-n-slate-12">
          {{ entry.carrier || $t('CONVERSATION_SIDEBAR.VTEX.STATUS_UNKNOWN') }}
        </span>
        <span v-if="entry.tracking_number">
          {{
            $t('CONVERSATION_SIDEBAR.VTEX.TRACKING_NUMBER', {
              number: entry.tracking_number,
            })
          }}
        </span>
        <span v-if="entry.shipping_status">{{ entry.shipping_status }}</span>
        <span v-if="entry.delivery_estimate">
          {{
            $t('CONVERSATION_SIDEBAR.VTEX.DELIVERY_ESTIMATE', {
              value: entry.delivery_estimate,
            })
          }}
        </span>
      </div>
    </div>

    <div
      v-if="
        canShowPayments &&
        (order.payment_status || order.payment_method || order.cancelled)
      "
      class="rounded-lg border border-n-weak p-2.5 flex flex-col gap-2"
    >
      <p
        class="text-xs font-semibold uppercase tracking-[0.08em] text-n-slate-10"
      >
        {{ $t('CONVERSATION_SIDEBAR.VTEX.PAYMENT_TITLE') }}
      </p>
      <div class="flex flex-wrap gap-2">
        <span
          v-if="order.payment_method"
          class="rounded-full bg-n-alpha-2 px-2 py-1 text-[11px] text-n-slate-11"
        >
          {{ order.payment_method }}
        </span>
        <span
          v-if="order.payment_status"
          class="rounded-full bg-n-alpha-2 px-2 py-1 text-[11px] text-n-slate-11"
        >
          {{ order.payment_status }}
        </span>
        <span
          v-if="order.cancelled"
          class="rounded-full bg-n-ruby-2 px-2 py-1 text-[11px] text-n-ruby-11"
        >
          {{ $t('CONVERSATION_SIDEBAR.VTEX.CANCELLED') }}
        </span>
      </div>
      <p v-if="order.cancellation_reason" class="text-sm text-n-slate-11">
        {{ order.cancellation_reason }}
      </p>
    </div>

    <div
      v-if="canShowReturns && order.returns"
      class="rounded-lg border border-n-weak p-2.5 flex flex-col gap-2"
    >
      <p
        class="text-xs font-semibold uppercase tracking-[0.08em] text-n-slate-10"
      >
        {{ $t('CONVERSATION_SIDEBAR.VTEX.RETURNS_TITLE') }}
      </p>
      <span class="text-sm font-medium text-n-slate-12">
        {{
          order.returns.status || $t('CONVERSATION_SIDEBAR.VTEX.STATUS_UNKNOWN')
        }}
      </span>
      <div v-if="returnsHistory.length" class="flex flex-col gap-2">
        <div
          v-for="entry in returnsHistory"
          :key="`${order.id}-${entry.created_at}-${entry.status}`"
          class="text-xs text-n-slate-11"
        >
          <span class="font-medium text-n-slate-12">{{ entry.status }}</span>
          <span v-if="entry.created_at || entry.reason">
            {{ formatReturnEntryMeta(entry) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
